import { useCallback, useEffect, useRef } from "react"

import { UserActivityEventType } from "../generated/graphql"
import { useTrackUserActivity } from "./tracking"

type CardRef = (element: HTMLElement | null) => void

// MM's threshold: at least half the card on screen.
const VISIBLE_SHARE = 0.5
// Browsers round the ratio they report at the threshold, sometimes just below it.
const ROUNDING = 0.01

// Records EventSeen once per mounted list for each event card at least half on screen. Ancestor
// overflow clips the intersection, so cards scrolled out of a horizontal ScrollMenu do not count.
// Pass `observe(eventId)` as each card's ref; the callback is cached per event, so re-renders do not
// re-observe.
export const useEventImpressions = (from: string) => {
  const track = useTrackUserActivity()
  const trackRef = useRef(track)
  trackRef.current = track
  const seen = useRef(new Set<string>())
  const cards = useRef(new Map<Element, string>())
  const refs = useRef(new Map<string, CardRef>())
  const observer = useRef<IntersectionObserver>()

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const eventId = cards.current.get(entry.target)
          // The observer also reports a card on observe and when it starts to overlap, with
          // isIntersecting true for any overlap: check the visible share itself.
          if (!eventId || entry.intersectionRatio < VISIBLE_SHARE - ROUNDING) continue
          io.unobserve(entry.target)
          if (seen.current.has(eventId)) continue
          seen.current.add(eventId)
          trackRef.current({ data: { eventId, from }, type: UserActivityEventType.EventSeen })
        }
      },
      { threshold: VISIBLE_SHARE },
    )
    observer.current = io
    // Card refs attach before this effect runs, so observe what has already mounted.
    for (const card of Array.from(cards.current.keys())) io.observe(card)
    return () => {
      io.disconnect()
      observer.current = undefined
    }
  }, [from])

  return useCallback((eventId: string) => {
    const cached = refs.current.get(eventId)
    if (cached) return cached
    let current: HTMLElement | null = null
    const ref: CardRef = (element) => {
      if (current) {
        observer.current?.unobserve(current)
        cards.current.delete(current)
      }
      current = element
      if (!element || seen.current.has(eventId)) return
      cards.current.set(element, eventId)
      observer.current?.observe(element)
    }
    refs.current.set(eventId, ref)
    return ref
  }, [])
}
